<template>
  <v-container class="fill-height">
    <v-row class="justify-center">
      <v-col cols="12" sm="6">
        <pt-sheet no-highlight>
          <v-card-title class="text-center px-8 mt-8">
            <img
              class="center"
              src="@/assets/vendorproof-shadowmark@2x.png"
              alt="VendorProof Logo"
              width="82px"
              height="82px"
            />
          </v-card-title>
          <v-card-subtitle class="my-4 px-8">
            <h2 class="title text-center">
              Connect a SignupID to your account.
            </h2>
          </v-card-subtitle>
          <v-alert
            v-if="this.isFeatureEnabled('MAINTENANCE_MODE')"
            type="warning"
          >
            Connecting a new SignupID is temporarily disabled while we perform
            maintenance. Please try again later.
          </v-alert>
          <v-alert :value="usedSignupCode !== null" type="error">
            {{ usedSignupCode }}
          </v-alert>
          <v-card-text class="px-8 mb-8">
            <v-form v-model="formValid">
              <v-text-field
                outlined
                disabled
                label="Username"
                v-model="username"
              />
              <p class="body-desktop-medium-bold-16">
                Enter your SignupID below.
              </p>
              <v-text-field
                outlined
                label="SignupID"
                v-model="signupId"
                :disabled="this.isFeatureEnabled('MAINTENANCE_MODE')"
                :rules="[
                  rules.required(),
                  rules.notBlank(),
                  rules.nChars('SignupId', 8)
                ]"
              />
              <p
                v-if="clientName"
                class="client-name body-desktop-small-regular-14"
              >
                This SignupID is registered with {{ clientName }}
              </p>
              <!-- This is to take up vertical space no matter what so the button doesn't bounce up and down -->
              <p v-else class="client-name body-desktop-small-regular-14">
                &nbsp;
              </p>
              <large-blue-call-to-action
                block
                @click.prevent="connectSignupId(signupId)"
                :loading="waitingForResponse"
                :disabled="connectDisabled"
                disabled-tooltip-message="Enter a SignupID"
              >
                Connect
              </large-blue-call-to-action>
            </v-form>
          </v-card-text>
        </pt-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PtSheet from "@/components/PtSheet.vue";
import LargeBlueCallToAction from "@/components/providertrust/buttons/LargeBlueCallToAction.vue";
import { get, post } from "@/services/request";
import { signupCodeAPI, validateSignupCodeAPI } from "@/services/api";
import * as rules from "@/services/rules";
import hasNotifications from "@/mixins/has-notifications";
import debounce from "lodash/debounce";
import store from "@/store";
import { getCurrentUser } from "@/services/shared";

export default {
  name: "ConnectSignupID",
  components: { LargeBlueCallToAction, PtSheet },
  mixins: [hasNotifications],
  data: () => ({
    username: "",
    signupId: "",
    usedSignupCode: null,
    waitingForResponse: false,
    formValid: false,
    clientName: "",
    rules
  }),
  computed: {
    connectDisabled() {
      return !this.formValid || this.waitingForResponse;
    }
  },
  created() {
    this.loadCurrentUser();
  },
  watch: {
    signupId: debounce(function(newSignupId) {
      if (newSignupId.length === 8) {
        this.getClientName(newSignupId);
      } else {
        this.clientName = ""; // Reset clientName if signupId is not of length 8
      }
    }, 500)
  },
  methods: {
    resetForApiCall() {
      this.waitingForResponse = true;
      this.usedSignupCode = null;
      this.clearMessage();
    },
    connectSignupId(signupCode) {
      this.resetForApiCall();
      post(validateSignupCodeAPI.getUrl(signupCode))
        .then(response => {
          if (response.data.status === "valid") {
            this.$router.push({
              name: "vendorSignup",
              params: { signupCode }
            });
          } else if (response.data.status === "used-signup-code") {
            this.usedSignupCode = response.data.message;
          }
        })
        .catch(error => {
          const errorMessage =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Error with that signup ID, please contact us for help.";

          this.showMessage({
            text: errorMessage,
            mode: "error"
          });
        })
        .finally(() => {
          this.waitingForResponse = false;
        });
    },
    getClientName(signupCode) {
      this.resetForApiCall();
      get(signupCodeAPI.getUrl(signupCode))
        .then(response => {
          if (response && response.data) {
            this.clientName = response.data.clientName;
          }
        })
        .catch(() => {
          this.clientName = "";
        })
        .finally(() => {
          this.waitingForResponse = false;
        });
    },
    loadCurrentUser() {
      store.dispatch("user/loadCurrentUser").then(() => {
        this.username = getCurrentUser().username;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.client-name {
  color: $color-vp-green-600;
  min-height: 21px;
  margin-top: -20px;
}
</style>
